/**
 * Pipedream app icon for automations UI surfaces.
 *
 * Renders the Pipedream-hosted CDN icon at the requested size, with a
 * letter-circle fallback when:
 *   - the slug doesn't resolve to metadata (Radix Avatar's fallback
 *     shows after the image fails / is unset),
 *   - the caller didn't pass `imgSrc` (e.g. from older API responses).
 *
 * Three call sites, all matching the design system's existing
 * <Avatar> primitive (Radix-backed, semantic-token coloring):
 *   - 14px in `AutomationsPage` row strips
 *   - 20px replacing the step-index circle in `AutomationDetailEditTab`
 *   - 16px next to the "Automated" badge in `Message.tsx`
 *
 * `referrerPolicy="no-referrer"` so we don't leak the user's Ano
 * session URL to Pipedream's CDN. `loading="lazy"` keeps the list
 * rendering cheap when there are many rows.
 */

import { cn } from "@/lib/utils";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

interface AppLogoProps {
  /** Pipedream app slug (e.g. "posthog", "linear"). Drives the fallback letter. */
  slug: string;
  /** CDN URL from `messages.metadata.apps_used` or `automation.apps_used`. */
  imgSrc?: string;
  /** Display name for the alt-text + tooltip. Falls back to the slug. */
  name?: string;
  /** Square pixel size. Defaults to 16 (the message-badge size). */
  size?: number;
  className?: string;
}

export function AppLogo({
  slug,
  imgSrc,
  name,
  size = 16,
  className,
}: AppLogoProps) {
  const altText = name ?? slug;
  const initial = slug.slice(0, 1).toUpperCase();

  return (
    <Avatar
      className={cn("shrink-0", className)}
      style={{ width: size, height: size }}
      title={altText}
    >
      {imgSrc ? (
        <AvatarImage
          src={imgSrc}
          alt={altText}
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ) : null}
      <AvatarFallback
        // Tighter typography than the default — letter-in-circle reads
        // best at small sizes when the letter takes ~60% of the diameter.
        className="bg-foreground/[0.06] text-foreground/70 font-medium tabular-nums"
        style={{ fontSize: Math.max(8, Math.round(size * 0.55)) }}
      >
        {initial}
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * Strip of app icons with a "+N more" overflow chip when the list
 * exceeds `max`. Used by `AutomationsPage` (max=3) and
 * `AutomationDetail` header. Spacing matches the existing pill row
 * (gap-1.5).
 */
export function AppLogoStrip({
  apps,
  size = 14,
  max = 3,
  className,
}: {
  apps: ReadonlyArray<{ slug: string; name?: string; img_src?: string }>;
  size?: number;
  max?: number;
  className?: string;
}) {
  // Defensive: callers pass `message.metadata?.apps_used ?? []` from a
  // jsonb column that's typed but not validated at the boundary. A
  // legacy / corrupt row could deliver a string ("[{...}]") or an
  // object instead of an array — both have `.length`/`.slice` (string
  // does, object doesn't), so the original `if (apps.length === 0)`
  // guard could fall through to `.map` on a non-array and crash the
  // page. Validate the runtime shape and bail to null on anything
  // unexpected.
  if (!Array.isArray(apps) || apps.length === 0) return null;
  const visible = apps.slice(0, max);
  const overflowCount = apps.length - visible.length;
  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      {visible.map((app) => (
        <AppLogo
          key={app.slug}
          slug={app.slug}
          imgSrc={app.img_src}
          name={app.name}
          size={size}
        />
      ))}
      {overflowCount > 0 ? (
        <span
          className="text-[10px] text-muted-foreground tabular-nums"
          title={apps
            .slice(max)
            .map((a) => a.name ?? a.slug)
            .join(", ")}
        >
          +{overflowCount}
        </span>
      ) : null}
    </span>
  );
}
